/**
 * Drag the Words parity: a translation needs the same gap / distractor counts
 * as the source. Must keep agreeing with the module's own `parse-text.js`.
 */
import { TranslatableField } from './types';

/** Library whose `textField` / `distractingWords` carry the puzzle. */
export const DRAG_TEXT_LIBRARY = 'H5P.DragTextSubactivity';

export const DRAG_TEXT_PARITY_FIELDS = {
  GAPS: 'text/textField',
  DISTRACTORS: 'text/distractingWords',
} as const;

export type DragTextParityKind =
  (typeof DRAG_TEXT_PARITY_FIELDS)[keyof typeof DRAG_TEXT_PARITY_FIELDS];

// A gap is an asterisk pair; `\*` is escaped and opens nothing.
const GAP_SPLIT = /(^|[^\\])(\*(?:[^*\\]|\\.)*?\*)/g;
const GAP_SEGMENT = /^\*(?:[^*\\]|\\.)*?\*$/;

/** Draggable gaps (`*word*`) in a Drag the Words text. */
export const countDragTextGaps = (text: string): number =>
  text
    .split(GAP_SPLIT)
    .filter(segment => !!segment && GAP_SEGMENT.test(segment)).length;

/** Distracting words — semicolon-separated, trailing separator ignored. */
export const countDragTextDistractors = (text: string): number => {
  const trimmed = text.replace(/;\s*$/, '');
  if (trimmed.trim() === '') {
    return 0;
  }
  return trimmed.split(';').filter(word => word.trim() !== '').length;
};

/** Which parity rule a translatable field is subject to, if any. */
export const dragTextParityKindOf = (
  field: TranslatableField,
): DragTextParityKind | null => {
  if (!field.item.library?.startsWith(DRAG_TEXT_LIBRARY)) {
    return null;
  }
  if (field.fieldName === DRAG_TEXT_PARITY_FIELDS.GAPS) {
    return DRAG_TEXT_PARITY_FIELDS.GAPS;
  }
  if (field.fieldName === DRAG_TEXT_PARITY_FIELDS.DISTRACTORS) {
    return DRAG_TEXT_PARITY_FIELDS.DISTRACTORS;
  }
  return null;
};

/** Count of the unit a parity kind measures. */
export const countForDragTextParity = (
  kind: DragTextParityKind,
  value: string,
): number =>
  kind === DRAG_TEXT_PARITY_FIELDS.GAPS
    ? countDragTextGaps(value)
    : countDragTextDistractors(value);
